import {
  TravelPlacePricingAndBookingDetailsPayload,
  TravelPlacePricingAndBookingDetailsFragment,
  TravelPlacePricingAndBookingDetailsFragmentDoc,
  TravelCurrencyCode,
  useTravelPlacesPricingAndBookingDetailsQuery,
  useTravelPlacesPricingAndBookingDetailsLazyQuery,
  AccommodationCatalogStayDatesInput,
  AccommodationCatalogUnitsOccupancyInput,
  TravelJoyPlacePricingAndBookingDetailsFragmentDoc,
  TravelJoyPlacePricingAndBookingDetailsFragment,
} from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addAction, addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';
import { getCalculatedPrice } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '../useMinimumPercentageOffToDisplay';
import { useSearchAccommodationsQueryMetadataPayload } from '../useSearchAccommodationsQueryMetadataPayload';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { Discount } from '@shared/components/DiscountsProvider/DiscountsProviderTypes';

export const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

type Overrides = {
  dates?: {
    checkIn: string;
    checkOut: string;
  };
  occupancies?: AccommodationCatalogUnitsOccupancyInput;
};

/**
 *
 * @param travelPlaceIds - array of travelPlaceIds to fetch pricing
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @param skipOnInitialLoad - Skip the query on initial load
 * @param overrides - Override default dates and occupancy
 * @param skipIfNoSearchParams - Skip if search params are not available
 * @param eventCurrency - Optional event-level currency from TravelSettings
 */

export const useTravelPlacesPricingAndBookingDetails = (
  travelPlaceIds: string[],
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
  skipOnInitialLoad = false,
  overrides: Overrides = {},
  skipIfNoSearchParams = false,
  eventCurrency?: TravelCurrencyCode | null,
) => {
  const { eventInfo, loading: isLoadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const derivedDates = useCheckInCheckoutDates({ eventDate });

  const checkInDate = overrides.dates?.checkIn || derivedDates.checkIn;
  const checkOutDate = overrides.dates?.checkOut || derivedDates.checkOut;

  const currencyCode = useTravelWhiteLabelCurrency(eventCurrency);
  const { value: localeCurrencyOnOrOff, loading: loadingUseLocaleCurrencyFlag } = useRemoteFeatureFlag('localeBasedHotelCurrency');

  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (loadingUseLocaleCurrencyFlag || isPreparingSearchMetadataPayload || !eventInfo?.eventId) {
      return undefined;
    }

    return {
      currencyCode: ((localeCurrencyOnOrOff === 'on' ? currencyCode : TravelCurrencyCode.USD) || TravelCurrencyCode.USD) as TravelCurrencyCode,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate,
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate,
        },
      },
      occupancies: overrides.occupancies || {
        occupancies: [
          {
            adults: 2,
            childrenAges: [],
          },
        ],
        unitCount: 1,
      },
      metadata: searchMetadata,
      ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelPlaceIds }),
      ...(travelPlaceIdType === 'joyPlace' && { joyPlaceIds: travelPlaceIds }),
    };
  }, [
    loadingUseLocaleCurrencyFlag,
    isPreparingSearchMetadataPayload,
    eventInfo?.eventId,
    localeCurrencyOnOrOff,
    currencyCode,
    checkInDate,
    checkOutDate,
    overrides.occupancies,
    searchMetadata,
    travelPlaceIdType,
    travelPlaceIds,
  ]);

  const {
    data,
    loading: travelPlacesPricingAndBookingDetailsLoading,
    called,
    error,
  } = useTravelPlacesPricingAndBookingDetailsQuery({
    batchMode: 'fast',
    variables: {
      payload: payload!,
    },
    skip: skipIfNoSearchParams || skipOnInitialLoad || !payload || !travelPlaceIds.length,
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPlacesPricingAndBookingDetails',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: payload?.currencyCode,
        metadata: payload?.metadata,
        accommodationPlaceIds: payload?.accommodationPlaceIds,
        joyPlaceIds: payload?.joyPlaceIds,
      });
    },
    onCompleted(data) {
      const accommodationPlaces = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces;
      if (!accommodationPlaces?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPlacesPricingAndBookingDetails',
          checkInDate,
          checkOutDate,
          currencyCode: payload?.currencyCode,
          metadata: payload?.metadata,
          accommodationPlaceIds: payload?.accommodationPlaceIds,
          joyPlaceIds: payload?.joyPlaceIds,
        });
      }
    },
  });

  const [
    getTravelPlacesPricingAndBookingDetails,
    {
      data: lazyData,
      loading: loadingGetTravelPlacesPricingAndBookingDetails,
      called: calledGetTravelPlacesPricingAndBookingDetails,
      error: errorGetTravelPlacesPricingAndBookingDetails,
    },
  ] = useTravelPlacesPricingAndBookingDetailsLazyQuery({
    batchMode: 'fast',
    onError: error => {
      addError(error, {
        context: 'useTravelPlacesPricingAndBookingDetailsQuery',
      });
    },
  });

  const fetchTravelPricingData = useCallback(
    (travelIdsToFetch: string[]) => {
      if (!travelIdsToFetch.length) {
        return;
      }

      getTravelPlacesPricingAndBookingDetails({
        variables: {
          payload: {
            ...payload,
            currencyCode: payload?.currencyCode as TravelCurrencyCode,
            dates: payload?.dates as AccommodationCatalogStayDatesInput,
            occupancies: payload?.occupancies as AccommodationCatalogUnitsOccupancyInput,
            ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelIdsToFetch }),
            ...(travelPlaceIdType === 'joyPlace' && { joyPlaceIds: travelIdsToFetch }),
          }!,
        },
      });
    },
    [payload, travelPlaceIdType, getTravelPlacesPricingAndBookingDetails],
  );

  const isLoading =
    travelPlacesPricingAndBookingDetailsLoading ||
    loadingUseLocaleCurrencyFlag ||
    isPreparingSearchMetadataPayload ||
    isLoadingEventInfo ||
    loadingGetTravelPlacesPricingAndBookingDetails;

  useEffect(() => {
    queryStatusVar({
      loading: isLoading,
      called: called || calledGetTravelPlacesPricingAndBookingDetails,
      error: error || errorGetTravelPlacesPricingAndBookingDetails,
    });
  }, [called, calledGetTravelPlacesPricingAndBookingDetails, error, errorGetTravelPlacesPricingAndBookingDetails, isLoading]);

  return {
    data: data || lazyData,
    loading: isLoading,
    error: error || errorGetTravelPlacesPricingAndBookingDetails,
    checkInDate,
    checkOutDate,
    fetchTravelPricingData,
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, travelPlaceId: string, travelPlaceIdType: 'accommodationPlace' | 'joyPlace') => {
  // Define the fragment
  const fragment = TravelPlacePricingAndBookingDetailsFragmentDoc;
  const joyPlaceFragment = TravelJoyPlacePricingAndBookingDetailsFragmentDoc;

  // Generate the cache ID based on the type and key fields
  let cacheId = `AccommodationPlacePricingAndBookingDetails:{"accommodationPlace":{"id":"${travelPlaceId}"}}`;
  if (travelPlaceIdType === 'joyPlace') {
    cacheId = `JoyPlacePricingAndBookingDetails:{"joyPlace":{"id":"${travelPlaceId}"}}`;
  }

  // Read the fragment from the cache
  const data = client.cache.readFragment<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>({
    id: cacheId,
    fragment: travelPlaceIdType === 'joyPlace' ? joyPlaceFragment : fragment,
  });

  return data;
};

type HookReturnType = Readonly<{
  price: Maybe<string>;
  priceInMinorUnits: Maybe<number>;
  strikeOutPrice: Maybe<string>;
  percentageOff: Maybe<number>;
  totalAmount: Maybe<string>;
  originalPrice: Maybe<string>;
  data: Maybe<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>;
  loading: boolean;
  soldOutUrl?: string;
  error?: Error;
}>;

const defaultReturn: HookReturnType = {
  price: undefined,
  priceInMinorUnits: undefined,
  strikeOutPrice: undefined,
  percentageOff: undefined,
  totalAmount: undefined,
  originalPrice: undefined,
  data: undefined,
  loading: true,
} as const;

/**
 *
 * @param travelPlaceId - accommodationPlaceId to fetch pricing for
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @returns
 */
export const useTravelPlacesPricingAndBookingDetailsFragment = (
  travelPlaceId: Maybe<string>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
  skip?: boolean,
  discount?: Discount,
): HookReturnType => {
  const complaintRef = useRef<boolean>(false);
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  const { minimumPercentageOffToDisplayValue, loadingMinimumPercentageOffToDisplay } = useMinimumPercentageOffToDisplay();

  if (loading || loadingMinimumPercentageOffToDisplay) {
    return {
      ...defaultReturn,
      loading: true,
    };
  }
  if (error) {
    return {
      ...defaultReturn,
      price: null,
      priceInMinorUnits: null,
      loading: false,
      error: error,
    };
  }
  if (skip) {
    return {
      ...defaultReturn,
      loading: false,
    };
  }
  const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);

  const { price, strikeOutPrice, percentageOff, totalAmount, minorUnits, originalPrice } = getCalculatedPrice(
    data,
    locale,
    minimumPercentageOffToDisplayValue,
    travelPlaceId,
    complaintRef,
    discount,
  );

  const soldOutUrl = extractHotelRedirects(data?.redirectMechanisms)?.hotelSearchPageUrl?.url;

  return { price, priceInMinorUnits: minorUnits, strikeOutPrice, percentageOff, totalAmount, data, loading: loading, soldOutUrl, originalPrice };
};

export const getTravelPlacesPricingAndBookingDetailsFragments = <T>(
  client: ApolloClient<object>,
  travelPlaceIds: ReadonlyArray<Maybe<string>>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
): T[] => {
  const results: T[] = [];

  travelPlaceIds.forEach(travelPlaceId => {
    if (!travelPlaceId) return;
    const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);
    if (data) results.push(data as T);
  });

  return results;
};
